/* ─────────────────────────────────────────────────────────
   Dawn DS — scroll motion engine (GSAP).

   Generic + editor-safe. Each section declares its treatment via
   data-ds-motion="editorial | signature | kinetic | none":
     editorial — calm fade-ups; headings fade (the hero headline still masks)
     signature — masked line reveals on headings + staggers
     kinetic   — signature + [data-slide] groups slide in + .js-wipe images wipe
     none      — no motion (content shown as-is)

   Hooks inside a section:
     .head-h            heading to reveal (.hero-h = always masked)
     .js-fu             element to fade up
     .js-stagger        container whose direct children stagger in
     .js-stagger[data-slide]  children slide in from the left (kinetic only)
     .js-wipe           element revealed with a clip-path wipe (kinetic only)

   Robustness:
     • prefers-reduced-motion → no motion, everything visible.
     • Theme editor (Shopify.designMode) → plays immediately (no scroll-gating)
       and re-runs on shopify:section:load / :unload so editing never breaks it.
     • A head-inline watchdog un-hides content if this file fails to load.
   ───────────────────────────────────────────────────────── */
(function () {
  if (window.__dsMaint) return; // a maintenance overlay owns the page — skip scroll motion behind it
  if (!window.gsap || !window.ScrollTrigger) return;
  window.__dsMotionReady = true; // tell the <head> watchdog the engine loaded

  var root = document.documentElement;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce) { root.classList.remove('js-motion'); return; } // show everything, no motion

  var design = !!(window.Shopify && window.Shopify.designMode);
  if (design) root.classList.remove('js-motion'); // editor: don't keep content hidden waiting for scroll

  var hasSplit = !!window.SplitText;
  gsap.registerPlugin(ScrollTrigger);
  if (hasSplit) gsap.registerPlugin(SplitText);

  var EASE = 'power3.out';
  var splitMap = new WeakMap(); // section -> [SplitText instances] for cleanup
  var gate = !design;           // live = scroll-gated; editor = play immediately

  // SplitText measures lines word by word, and a comma or full stop right after </em> counts as
  // its own word, so it can wrap to the start of the next line. Move it inside the em first.
  function glueTrailingPunctuation(h) {
    h.querySelectorAll('em, strong').forEach(function (el) {
      var next = el.nextSibling;
      if (!next || next.nodeType !== 3) return;
      var m = next.nodeValue.match(/^[,.;:!?\u2026)\]\u201D\u2019]+/);
      if (!m) return;
      el.appendChild(document.createTextNode(m[0]));
      next.nodeValue = next.nodeValue.slice(m[0].length);
    });
  }

  function maskHead(h, store) {
    if (!hasSplit) { fadeIn(h, 14); return; }
    glueTrailingPunctuation(h);
    var s = SplitText.create(h, { type: 'lines', mask: 'lines', linesClass: 'ds-line' });
    store.push(s);
    gsap.set(h, { opacity: 1 });
    var v = { yPercent: 120, opacity: 0, duration: 0.9, ease: EASE, stagger: 0.1 };
    if (gate) v.scrollTrigger = { trigger: h, start: 'top 84%', once: true };
    gsap.from(s.lines, v);
  }
  function fadeIn(el, y) {
    gsap.set(el, { opacity: 1 });
    var v = { y: (y || 16), opacity: 0, duration: 0.7, ease: EASE };
    if (gate) v.scrollTrigger = { trigger: el, start: 'top 88%', once: true };
    gsap.from(el, v);
  }
  function staggerGroup(g, x) {
    var items = Array.prototype.slice.call(g.children);
    if (!items.length) return;
    gsap.set(items, { opacity: 0, y: 28, x: x || 0 });
    if (gate) {
      ScrollTrigger.batch(items, {
        start: 'top 86%',
        onEnter: function (b) { gsap.to(b, { opacity: 1, y: 0, x: 0, duration: 0.7, ease: EASE, stagger: 0.1, overwrite: true }); }
      });
    } else {
      gsap.to(items, { opacity: 1, y: 0, x: 0, duration: 0.7, ease: EASE, stagger: 0.1 });
    }
  }
  function wipeIn(m) {
    gsap.set(m, { clipPath: 'inset(0 0 100% 0)' });
    var v = { clipPath: 'inset(0 0 0% 0)', duration: 0.9, ease: EASE };
    if (gate) v.scrollTrigger = { trigger: m, start: 'top 82%', once: true };
    gsap.to(m, v);
  }

  function clearSection(sec) {
    ScrollTrigger.getAll().forEach(function (t) { if (t.trigger && sec.contains(t.trigger)) t.kill(); });
    var arr = splitMap.get(sec);
    if (arr) arr.forEach(function (s) { try { s.revert(); } catch (e) {} });
    splitMap.set(sec, []);
  }

  function reveal(sec) {
    var mode = sec.getAttribute('data-ds-motion') || 'none';
    if (mode === 'none') return;
    clearSection(sec);
    var store = [];
    sec.querySelectorAll('.head-h').forEach(function (h) {
      if (mode === 'editorial' && !h.classList.contains('hero-h')) fadeIn(h, 14);
      else maskHead(h, store);
    });
    sec.querySelectorAll('.js-fu').forEach(function (el) { fadeIn(el, 16); });
    sec.querySelectorAll('.js-stagger').forEach(function (g) {
      var x = (mode === 'kinetic' && g.hasAttribute('data-slide')) ? -26 : 0;
      staggerGroup(g, x);
    });
    if (mode === 'kinetic') sec.querySelectorAll('.js-wipe').forEach(wipeIn);
    splitMap.set(sec, store);
  }

  function initAll() {
    document.querySelectorAll('[data-ds-motion]').forEach(reveal);
    ScrollTrigger.refresh();
  }

  // Debounced refresh for layout changes (reorder, resize handled by GSAP itself).
  var rt;
  function refreshSoon() { clearTimeout(rt); rt = setTimeout(function () { ScrollTrigger.refresh(); }, 200); }

  if (document.fonts && document.fonts.ready) document.fonts.ready.then(initAll);
  else if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', initAll);

  // Images (hero art + spotlight photos) decode after fonts.ready, shifting layout
  // downward. Without a re-measure, fade-up triggers keep their stale start positions
  // and in-viewport elements can stay stuck hidden (blank voids on mobile). Re-measure
  // once everything has loaded so every trigger reflects the final layout.
  window.addEventListener('load', function () { ScrollTrigger.refresh(); });

  // ── Shopify theme editor lifecycle (keeps motion correct while editing) ──
  document.addEventListener('shopify:section:load', function (e) {
    var t = e.target;
    var sec = (t.matches && t.matches('[data-ds-motion]')) ? t : (t.querySelector ? t.querySelector('[data-ds-motion]') : null);
    if (sec) { reveal(sec); refreshSoon(); }
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var sec = e.target.querySelector ? e.target.querySelector('[data-ds-motion]') : null;
    if (sec) clearSection(sec);
    refreshSoon();
  });
  document.addEventListener('shopify:section:reorder', refreshSoon);
})();
